import * as XLSX from 'xlsx';

// Ruta absoluta web apuntando a la nueva ubicación en la raíz de api/
const API= "/api/caja"; 

/**
 * Consulta las ventas de caja filtradas por un rango de fechas
 * @param {string} desde - Fecha inicial en formato YYYY-MM-DD
 * @param {string} hasta - Fecha final en formato YYYY-MM-DD
 * @returns {Promise<Array>} Lista de cabeceras de ventas encontradas
 */
export const getReporteCajaPorFechas = async (desde, hasta) => {
  try {
    const url = `${API}?desde=${desde}&hasta=${hasta}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    // Detenemos cualquier respuesta HTML de error (como el 404 o 500) antes de procesar el JSON
    if (!response.ok) {
      throw new Error(`Error al consultar datos de caja (Código: ${response.status})`);
    }
    
    return await response.json();
  } catch (error) {
    throw new Error(error.message);
  }
};

/**
 * Genera y descarga un archivo Excel con el balance de caja y el total acumulado (excluyendo anuladas)
 * @param {Array} ventas - Registros devueltos por la API de caja
 * @param {string} desde - Fecha de inicio seleccionada en el filtro
 * @param {string} hasta - Fecha de fin seleccionada en el filtro
 */
export const exportarCajaExcel = (ventas, desde, hasta) => {
  try {
    if (!ventas || ventas.length === 0) {
      throw new Error("No hay registros de ventas en este rango de fechas para exportar.");
    }

    // 1. Mapeamos y estructuramos las filas de las ventas para el Excel
    const datosFormateados = ventas.map((v) => ({
      "N° Venta": `#${v.venta_id}`,
      "Cajero / Usuario": v.usuario,
      "Fecha de emision": v.fecha_completa,
      "Estado Venta": v.estado.toUpperCase(),
      "Monto Total": Number(v.total || 0)
    }));

    // 2. Calculamos la suma total EXCLUYENDO las ventas anuladas
    // Usamos .includes('ANULADA') por si el estado viene como 'ANULADA' o 'VENTA ANULADA'
    const totalCajaAcumulado = ventas.reduce((acc, v) => {
      const estadoMayus = (v.estado || "").toUpperCase();
      const esAnulada = estadoMayus.includes("ANULADA");
      
      return acc + (esAnulada ? 0 : Number(v.total || 0));
    }, 0);

    // 3. Añadimos una fila de separación estéticamente limpia
    datosFormateados.push({
      "N° Venta": "",
      "Cajero / Usuario": "",
      "Fecha de emision": "", // Asegurado que coincida con la cabecera original
      "Estado Venta": "",
      "Monto Total": null
    });

    // 4. Insertamos la fila de balance final del reporte
    datosFormateados.push({
      "N° Venta": "BALANCE TOTAL",
      "Cajero / Usuario": `Desde: ${desde} | Hasta: ${hasta}`,
      "Fecha de emision": "",
      "Estado Venta": "TOTAL EFECTIVO REAL:", // Cambiado para indicar que descuenta anuladas
      "Monto Total": `S/ ${totalCajaAcumulado.toFixed(2)}`
    });

    // 5. Transformamos de JSON a hoja de Excel
    const hoja = XLSX.utils.json_to_sheet(datosFormateados);
    const libro = XLSX.utils.book_new();
    
    XLSX.utils.book_append_sheet(libro, hoja, "Movimiento de Caja");

    // 6. Descarga automática del archivo usando el rango consultado
    const nombreArchivo = `Reporte_Caja_${desde}_a_${hasta}.xlsx`;
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIOS) {
        // En iOS descargas directo usando la gestión de archivos nativa de Safari
        XLSX.writeFile(libro, nombreArchivo);
    } else {
        // En Android / Escritorio, generamos el buffer y forzamos la descarga con su nombre real
        const excelBuffer = XLSX.write(libro, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        const url = URL.createObjectURL(blob);
        
        // Creamos un elemento 'a' invisible en memoria
        const link = document.createElement('a');
        link.href = url;
        link.download = nombreArchivo; // 👈 Aquí le inyectamos el nombre real del reporte
        
        // Añadimos al documento, hacemos click y removemos inmediatamente
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Limpiamos el objeto URL de la memoria de manera segura
        setTimeout(() => URL.revokeObjectURL(url), 250); 
    }

  } catch (error) {
    throw new Error("Error al generar el Excel de caja: " + error.message);
  }
};